/**
 * CompanyEditPage — refonte 9.P.2.R1 UX/layout revamp.
 *
 * Pattern UX parité CompanyCreatePage :
 *   - Layout 2-col desktop : section nav sidebar gauche + form principal droite
 *   - Sticky save bar bottom (Cancel + Save toujours visibles)
 *   - Confirmation unsaved changes via `confirm()` natif au Cancel
 *   - <details> + CompanyFormDetails wrapper moderne
 *
 * Logique métier strictement préservée :
 *   - useCompany / useUpdateCompany
 *   - fromCompany / toPayload helpers (D-NEW-D polish 4 — `""` → `null` explicit)
 *   - D-NEW-D Phase 1 cross-field validation onBlur + onChange + onSubmit
 *   - localBnError pour message inline
 *   - aria-invalid / aria-describedby
 *   - backend fieldErrors pipeline + bandeau bilingue
 */

import { type FormEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import { Schemas } from "@kollectivia/shared";

import { ApiException } from "../../lib/api";
import { getApiErrorMessage, normalizeLang } from "../../lib/apiErrorMessage";
import { type Company, useCompany, useUpdateCompany } from "../../lib/queries/companies";
import { toast } from "../../lib/toast";
import { useUnsavedChangesGuard } from "../../lib/unsavedChanges/UnsavedChangesContext";

import { CompanyFormDetails } from "./_components/CompanyFormDetails";
import { CompanySectionNav } from "./_components/CompanySectionNav";

type FormState = {
  legal_name: string;
  trade_name: string;
  business_number: string;
  identification_type: "" | "NEQ" | "BN" | "CORP_NUM_CA" | "EIN_US" | "VAT_EU" | "OTHER";
  industry: string;
  founded_year: string;
  address_street: string;
  address_city: string;
  address_province: string;
  address_postal: string;
  address_country: string;
  phone: string;
  fax: string;
  email: string;
  website: string;
  plan_admin_name: string;
  plan_admin_email: string;
  language: "fr" | "en";
  employee_count: string;
  has_seasonal: boolean;
  has_commission: boolean;
  has_unionized: boolean;
  has_remote_abroad: boolean;
  considers_remote_abroad: boolean;
  family_ties: boolean;
  family_ties_note: string;
  has_current_plan: boolean;
  current_insurer: string;
  current_policy_number: string;
  current_since_date: string;
  current_renewal_date: string;
  notes: string;
};

function fromCompany(c: Company): FormState {
  return {
    legal_name: c.legal_name,
    trade_name: c.trade_name ?? "",
    business_number: c.business_number ?? "",
    identification_type:
      c.identification_type === "NEQ" ||
      c.identification_type === "BN" ||
      c.identification_type === "CORP_NUM_CA" ||
      c.identification_type === "EIN_US" ||
      c.identification_type === "VAT_EU" ||
      c.identification_type === "OTHER"
        ? c.identification_type
        : "",
    industry: c.industry ?? "",
    founded_year: c.founded_year != null ? String(c.founded_year) : "",
    address_street: c.address_street ?? "",
    address_city: c.address_city ?? "",
    address_province: c.address_province ?? "",
    address_postal: c.address_postal ?? "",
    address_country: c.address_country ?? "",
    phone: c.phone ?? "",
    fax: c.fax ?? "",
    email: c.email ?? "",
    website: c.website ?? "",
    plan_admin_name: c.plan_admin_name ?? "",
    plan_admin_email: c.plan_admin_email ?? "",
    language: c.language,
    employee_count: c.employee_count != null ? String(c.employee_count) : "",
    has_seasonal: c.has_seasonal ?? false,
    has_commission: c.has_commission ?? false,
    has_unionized: c.has_unionized ?? false,
    has_remote_abroad: c.has_remote_abroad ?? false,
    considers_remote_abroad: c.considers_remote_abroad ?? false,
    family_ties: c.family_ties ?? false,
    family_ties_note: c.family_ties_note ?? "",
    has_current_plan: c.has_current_plan ?? false,
    current_insurer: c.current_insurer ?? "",
    current_policy_number: c.current_policy_number ?? "",
    current_since_date: c.current_since_date ?? "",
    current_renewal_date: c.current_renewal_date ?? "",
    notes: c.notes ?? "",
  };
}

function toPayload(s: FormState): Record<string, unknown> {
  const strOrUndef = (v: string) => {
    const t = v.trim();
    return t === "" ? undefined : t;
  };
  const numOrUndef = (v: string) => {
    const t = v.trim();
    if (t === "") return undefined;
    const n = Number(t);
    return Number.isFinite(n) ? n : undefined;
  };

  return {
    legal_name: s.legal_name.trim(),
    trade_name: strOrUndef(s.trade_name),
    business_number: strOrUndef(s.business_number),
    // D-NEW-D Phase 1 polish 4 — sur Edit, `""` (placeholder) signifie
    // « effacer la valeur » → on envoie `null` explicite pour que le PATCH
    // SQL fasse `SET identification_type = NULL`.
    identification_type: s.identification_type === "" ? null : s.identification_type,
    industry: strOrUndef(s.industry),
    founded_year: numOrUndef(s.founded_year),
    address_street: strOrUndef(s.address_street),
    address_city: strOrUndef(s.address_city),
    address_province: strOrUndef(s.address_province),
    address_postal: strOrUndef(s.address_postal),
    address_country: strOrUndef(s.address_country),
    phone: strOrUndef(s.phone),
    fax: strOrUndef(s.fax),
    email: strOrUndef(s.email),
    website: strOrUndef(s.website),
    plan_admin_name: strOrUndef(s.plan_admin_name),
    plan_admin_email: strOrUndef(s.plan_admin_email),
    language: s.language,
    employee_count: numOrUndef(s.employee_count),
    has_seasonal: s.has_seasonal,
    has_commission: s.has_commission,
    has_unionized: s.has_unionized,
    has_remote_abroad: s.has_remote_abroad,
    considers_remote_abroad: s.considers_remote_abroad,
    family_ties: s.family_ties,
    family_ties_note: s.family_ties ? strOrUndef(s.family_ties_note) : undefined,
    has_current_plan: s.has_current_plan,
    current_insurer: s.has_current_plan ? strOrUndef(s.current_insurer) : undefined,
    current_policy_number: s.has_current_plan ? strOrUndef(s.current_policy_number) : undefined,
    current_since_date: s.has_current_plan ? strOrUndef(s.current_since_date) : undefined,
    current_renewal_date: s.has_current_plan ? strOrUndef(s.current_renewal_date) : undefined,
    notes: strOrUndef(s.notes),
  };
}

export function CompanyEditPage() {
  const { id } = useParams<{ id: string }>();
  const { t, i18n } = useTranslation();
  const lang = normalizeLang(i18n.resolvedLanguage);
  const navigate = useNavigate();
  const company = useCompany(id);
  const update = useUpdateCompany(id ?? "");

  const [form, setForm] = useState<FormState | null>(null);
  const [dirty, setDirty] = useState(false);
  const [localBnError, setLocalBnError] = useState<string | null>(null);

  useEffect(() => {
    if (company.data && form === null) {
      setForm(fromCompany(company.data));
    }
  }, [company.data, form]);

  // 9.P.2.R2 — guard global unsaved changes. **Doit être appelé avant tout
  // early return** pour respecter rules-of-hooks (ordre stable inter-renders).
  // Le message est conditionnel sur `form?.legal_name` ; si form encore null
  // on passe une chaîne vide acceptée par le hook (no-op si dirty=false).
  const { allowNavigation } = useUnsavedChangesGuard(
    dirty,
    form
      ? t("companies.edit.unsaved_message", { name: form.legal_name })
      : "",
  );

  // Archived = read-only (post-smoke R2). Si l'utilisateur arrive sur
  // /companies/:id/edit pour une company archived (NavLink direct, bookmark,
  // URL collée), on redirige vers /:id avec un toast info. Pas de form
  // exposé tant que la company n'est pas restaurée (backend GO pending).
  useEffect(() => {
    if (company.data && company.data.status === "archived" && id) {
      toast.info(t("companies.edit.archived_blocked"));
      allowNavigation();
      navigate(`/companies/${id}`, { replace: true });
    }
  }, [company.data, id, navigate, t, allowNavigation]);

  if (company.isLoading || !form) {
    return (
      <div className="mx-auto w-full max-w-4xl">
        <div className="h-32 rounded-xl bg-slate-100 motion-safe:animate-pulse motion-reduce:animate-none" />
      </div>
    );
  }

  if (company.error) {
    return (
      <div className="mx-auto w-full max-w-4xl">
        <div
          role="alert"
          className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700"
        >
          {company.error instanceof ApiException
            ? getApiErrorMessage(company.error, lang) || t("errors.generic")
            : t("errors.generic")}
        </div>
      </div>
    );
  }

  const setField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((s) => (s ? { ...s, [key]: value } : s));
    setDirty(true);
    if (key === "business_number" || key === "identification_type") {
      setLocalBnError(null);
    }
  };

  const onCancel = () => navigate(`/companies/${id}`);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!form.legal_name.trim() || !id) return;

    if (form.identification_type && form.business_number.trim() !== "") {
      const err = Schemas.validateBusinessNumberForType(
        form.business_number.trim(),
        form.identification_type,
      );
      if (err === "invalid_format") {
        setLocalBnError(
          `Invalid format for identification_type=${form.identification_type}`,
        );
        return;
      }
    }

    try {
      await update.mutateAsync(toPayload(form));
      // 9.P.2.R2 fix : bypass single-shot du blocker avant navigate post-save
      // (anti race React batching, cf. CompanyCreatePage).
      allowNavigation();
      setDirty(false);
      navigate(`/companies/${id}`);
    } catch {
      // erreur visible via update.error
    }
  };

  const apiError =
    update.error instanceof ApiException ? update.error.apiError : null;
  const apiErrorText = getApiErrorMessage(update.error, lang);
  const fieldErrors =
    apiError?.details && typeof apiError.details === "object"
      ? (apiError.details as { fieldErrors?: Record<string, string[]> })
          .fieldErrors
      : undefined;
  const fieldError = (key: string) =>
    fieldErrors?.[key]?.[0] ? (
      <span className="mt-1 block text-xs text-red-600">{fieldErrors[key][0]}</span>
    ) : null;

  const navItems = [
    { anchorId: "identity", label: t("companies.create.section.identity") },
    { anchorId: "address", label: t("companies.create.section.address") },
    { anchorId: "contact", label: t("companies.create.section.contact") },
    { anchorId: "workforce", label: t("companies.create.section.workforce") },
    { anchorId: "current_plan", label: t("companies.create.section.current_plan") },
    { anchorId: "notes", label: t("companies.create.section.notes") },
  ];

  return (
    <div className="mx-auto w-full max-w-4xl pb-24 lg:max-w-screen-xl">
      {/* Sticky page header — `top-16` mobile / `lg:top-0` desktop (main scroll-container 9.P.2.R2) */}
      <header className="sticky top-16 z-10 -mx-4 mb-6 border-b border-slate-200 bg-slate-50/95 px-4 py-4 backdrop-blur-sm lg:-mx-8 lg:top-0 lg:px-8 lg:py-5">
        <h1 className="text-3xl font-semibold tracking-tight text-slate-900">
          {t("companies.edit.title")}
        </h1>
        <p className="mt-1 text-sm text-slate-600">{form.legal_name}</p>
      </header>

      {apiError && !fieldErrors && (
        <div
          role="alert"
          className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
        >
          {apiErrorText || apiError.message || t("errors.generic")}
        </div>
      )}

      {/* 2-col layout : section nav left + form right */}
      <div className="grid gap-6 lg:grid-cols-[14rem_1fr] lg:gap-8">
        <div className="hidden lg:block">
          <div className="sticky top-40">
            <CompanySectionNav
              items={navItems}
              ariaLabel={t("companies.create.section_nav_label")}
            />
          </div>
        </div>

        <form onSubmit={onSubmit} className="space-y-4">
          <CompanyFormDetails
            id="identity"
            title={t("companies.create.section.identity")}
            open={true}
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <label className="block sm:col-span-2">
                <span className="label">{t("companies.fields.legal_name")} *</span>
                <input
                  className="input"
                  value={form.legal_name}
                  onChange={(e) => setField("legal_name", e.target.value)}
                  required
                />
                {fieldError("legal_name")}
              </label>
              <label className="block">
                <span className="label">{t("companies.fields.trade_name")}</span>
                <input
                  className="input"
                  value={form.trade_name}
                  onChange={(e) => setField("trade_name", e.target.value)}
                />
              </label>
              <label className="block">
                <span className="label">{t("companies.fields.identification_type")}</span>
                <select
                  className="input"
                  value={form.identification_type}
                  onChange={(e) => {
                    const newType = e.target.value as FormState["identification_type"];
                    setField("identification_type", newType);
                    if (form.business_number.trim() === "" || newType === "") {
                      setLocalBnError(null);
                      return;
                    }
                    const err = Schemas.validateBusinessNumberForType(
                      form.business_number.trim(),
                      newType,
                    );
                    setLocalBnError(
                      err === "invalid_format"
                        ? `Invalid format for identification_type=${newType}`
                        : null,
                    );
                  }}
                >
                  <option value="">
                    {t("companies.fields.identification_type_placeholder")}
                  </option>
                  <option value="NEQ">
                    {t("companies.fields.identification_type_options.NEQ")}
                  </option>
                  <option value="BN">
                    {t("companies.fields.identification_type_options.BN")}
                  </option>
                  <option value="CORP_NUM_CA">
                    {t("companies.fields.identification_type_options.CORP_NUM_CA")}
                  </option>
                  <option value="EIN_US">
                    {t("companies.fields.identification_type_options.EIN_US")}
                  </option>
                  <option value="VAT_EU">
                    {t("companies.fields.identification_type_options.VAT_EU")}
                  </option>
                  <option value="OTHER">
                    {t("companies.fields.identification_type_options.OTHER")}
                  </option>
                </select>
              </label>
              <label className="block">
                <span className="label">{t("companies.fields.business_number")}</span>
                <input
                  id="business_number"
                  className="input"
                  value={form.business_number}
                  onChange={(e) => setField("business_number", e.target.value)}
                  onBlur={(e) => {
                    const bnValue = e.target.value.trim();
                    if (bnValue === "" || form.identification_type === "") {
                      setLocalBnError(null);
                      return;
                    }
                    const err = Schemas.validateBusinessNumberForType(
                      bnValue,
                      form.identification_type,
                    );
                    setLocalBnError(
                      err === "invalid_format"
                        ? `Invalid format for identification_type=${form.identification_type}`
                        : null,
                    );
                  }}
                  aria-invalid={localBnError ? "true" : undefined}
                  aria-describedby={localBnError ? "business_number-error" : undefined}
                />
                <span className="mt-1 block text-xs text-slate-500">
                  {form.identification_type
                    ? t(
                        `companies.fields.identification_type_help_dynamic.${form.identification_type}`,
                      )
                    : t("companies.fields.business_number_help")}
                </span>
                {(() => {
                  const rawMsg = localBnError ?? fieldErrors?.business_number?.[0];
                  if (!rawMsg) return null;
                  const m = rawMsg.match(/^Invalid format for identification_type=(\w+)$/);
                  const displayed = m
                    ? t("companies.fields.identification_type_error_invalid_format", {
                        type: m[1],
                      })
                    : rawMsg;
                  return (
                    <span
                      id="business_number-error"
                      className="mt-1 block text-xs text-red-600"
                    >
                      {displayed}
                    </span>
                  );
                })()}
              </label>
              <label className="block">
                <span className="label">{t("companies.fields.industry")}</span>
                <input
                  className="input"
                  value={form.industry}
                  onChange={(e) => setField("industry", e.target.value)}
                />
              </label>
              <label className="block">
                <span className="label">{t("companies.fields.founded_year")}</span>
                <input
                  type="number"
                  min={1800}
                  max={new Date().getFullYear()}
                  className="input"
                  value={form.founded_year}
                  onChange={(e) => setField("founded_year", e.target.value)}
                />
              </label>
              <label className="block">
                <span className="label">{t("companies.fields.language")}</span>
                <select
                  className="input"
                  value={form.language}
                  onChange={(e) => setField("language", e.target.value as "fr" | "en")}
                >
                  <option value="fr">Français</option>
                  <option value="en">English</option>
                </select>
              </label>
              <label className="block">
                <span className="label">{t("companies.fields.employee_count")}</span>
                <input
                  type="number"
                  min={0}
                  className="input"
                  value={form.employee_count}
                  onChange={(e) => setField("employee_count", e.target.value)}
                />
              </label>
            </div>
          </CompanyFormDetails>

          <CompanyFormDetails
            id="address"
            title={t("companies.create.section.address")}
            open={true}
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <label className="block sm:col-span-2">
                <span className="label">{t("companies.fields.address_street")}</span>
                <input
                  className="input"
                  value={form.address_street}
                  onChange={(e) => setField("address_street", e.target.value)}
                />
              </label>
              <label className="block">
                <span className="label">{t("companies.fields.address_city")}</span>
                <input
                  className="input"
                  value={form.address_city}
                  onChange={(e) => setField("address_city", e.target.value)}
                />
              </label>
              <label className="block">
                <span className="label">{t("companies.fields.address_province")}</span>
                <input
                  className="input"
                  value={form.address_province}
                  onChange={(e) => setField("address_province", e.target.value)}
                />
              </label>
              <label className="block">
                <span className="label">{t("companies.fields.address_postal")}</span>
                <input
                  className="input"
                  value={form.address_postal}
                  onChange={(e) => setField("address_postal", e.target.value)}
                />
              </label>
              <label className="block">
                <span className="label">{t("companies.fields.address_country")}</span>
                <input
                  className="input"
                  value={form.address_country}
                  onChange={(e) => setField("address_country", e.target.value)}
                />
              </label>
            </div>
          </CompanyFormDetails>

          <CompanyFormDetails
            id="contact"
            title={t("companies.create.section.contact")}
            open={true}
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <label className="block">
                <span className="label">{t("companies.fields.phone")}</span>
                <input className="input" value={form.phone} onChange={(e) => setField("phone", e.target.value)} />
              </label>
              <label className="block">
                <span className="label">{t("companies.fields.fax")}</span>
                <input className="input" value={form.fax} onChange={(e) => setField("fax", e.target.value)} />
              </label>
              <label className="block">
                <span className="label">{t("companies.fields.email")}</span>
                <input type="email" className="input" value={form.email} onChange={(e) => setField("email", e.target.value)} />
                {fieldError("email")}
              </label>
              <label className="block">
                <span className="label">{t("companies.fields.website")}</span>
                <input className="input" value={form.website} onChange={(e) => setField("website", e.target.value)} />
              </label>
              <label className="block">
                <span className="label">{t("companies.fields.plan_admin_name")}</span>
                <input className="input" value={form.plan_admin_name} onChange={(e) => setField("plan_admin_name", e.target.value)} />
              </label>
              <label className="block">
                <span className="label">{t("companies.fields.plan_admin_email")}</span>
                <input type="email" className="input" value={form.plan_admin_email} onChange={(e) => setField("plan_admin_email", e.target.value)} />
                {fieldError("plan_admin_email")}
              </label>
            </div>
          </CompanyFormDetails>

          <CompanyFormDetails
            id="workforce"
            title={t("companies.create.section.workforce")}
            open={true}
          >
            <div className="space-y-3">
              <Checkbox checked={form.has_seasonal} onChange={(v) => setField("has_seasonal", v)} label={t("companies.fields.has_seasonal")} />
              <Checkbox checked={form.has_commission} onChange={(v) => setField("has_commission", v)} label={t("companies.fields.has_commission")} />
              <Checkbox checked={form.has_unionized} onChange={(v) => setField("has_unionized", v)} label={t("companies.fields.has_unionized")} />
              <Checkbox checked={form.has_remote_abroad} onChange={(v) => setField("has_remote_abroad", v)} label={t("companies.fields.has_remote_abroad")} />
              <Checkbox checked={form.considers_remote_abroad} onChange={(v) => setField("considers_remote_abroad", v)} label={t("companies.fields.considers_remote_abroad")} />
              <Checkbox checked={form.family_ties} onChange={(v) => setField("family_ties", v)} label={t("companies.fields.family_ties")} />
              {form.family_ties && (
                <label className="block">
                  <span className="label">{t("companies.fields.family_ties_note")}</span>
                  <textarea
                    className="input min-h-[4rem]"
                    value={form.family_ties_note}
                    onChange={(e) => setField("family_ties_note", e.target.value)}
                  />
                </label>
              )}
            </div>
          </CompanyFormDetails>

          <CompanyFormDetails
            id="current_plan"
            title={t("companies.create.section.current_plan")}
            open={true}
          >
            <div className="space-y-3">
              <Checkbox checked={form.has_current_plan} onChange={(v) => setField("has_current_plan", v)} label={t("companies.fields.has_current_plan")} />
              {form.has_current_plan && (
                <div className="grid gap-4 sm:grid-cols-2">
                  <label className="block">
                    <span className="label">{t("companies.fields.current_insurer")}</span>
                    <input className="input" value={form.current_insurer} onChange={(e) => setField("current_insurer", e.target.value)} />
                  </label>
                  <label className="block">
                    <span className="label">{t("companies.fields.current_policy_number")}</span>
                    <input className="input" value={form.current_policy_number} onChange={(e) => setField("current_policy_number", e.target.value)} />
                  </label>
                  <label className="block">
                    <span className="label">{t("companies.fields.current_since_date")}</span>
                    <input type="date" className="input" value={form.current_since_date} onChange={(e) => setField("current_since_date", e.target.value)} />
                  </label>
                  <label className="block">
                    <span className="label">{t("companies.fields.current_renewal_date")}</span>
                    <input type="date" className="input" value={form.current_renewal_date} onChange={(e) => setField("current_renewal_date", e.target.value)} />
                  </label>
                </div>
              )}
            </div>
          </CompanyFormDetails>

          <CompanyFormDetails
            id="notes"
            title={t("companies.create.section.notes")}
            open={true}
          >
            <label className="block">
              <span className="label">{t("companies.fields.notes")}</span>
              <textarea
                className="input min-h-[6rem]"
                value={form.notes}
                onChange={(e) => setField("notes", e.target.value)}
              />
            </label>
          </CompanyFormDetails>

          {/* Sticky save bar bottom */}
          <div className="sticky bottom-0 z-10 -mx-4 mt-6 border-t border-slate-200 bg-white/95 px-4 py-3 backdrop-blur-sm lg:-mx-8 lg:px-8">
            <div className="flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={onCancel}
                disabled={update.isPending}
                className="rounded-lg px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 disabled:opacity-50 touch-manipulation"
              >
                {t("common.cancel")}
              </button>
              <button
                type="submit"
                disabled={update.isPending || !form.legal_name.trim()}
                className="inline-flex touch-manipulation items-center gap-2 rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-brand-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-slate-300"
              >
                {update.isPending ? t("app.loading") : t("common.save")}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

function Checkbox({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
}) {
  return (
    <label className="flex items-start gap-2">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-1 h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
      />
      <span className="text-sm text-slate-700">{label}</span>
    </label>
  );
}
